'use client';

import { Button } from '@/components/ui/button';
import { IProperty } from '@/lib/types';
import Link from 'next/link';
import { PropertyCard } from '../properties/PropertyCard';
import PropertyCardPremium from '../properties/PropertyCardPremium';
import { useScrollAnimation, useStaggeredScrollAnimation } from '@/hooks/use-scroll-animation';

interface FeaturedPropertiesClientProps {
  properties: IProperty[];
}

/**
 * Client-side component for FeaturedProperties
 * Handles scroll animations for property cards
 */
export function FeaturedPropertiesClient({ properties }: FeaturedPropertiesClientProps) {
  const titleRef = useScrollAnimation<HTMLDivElement>('fade-in-down', {
    threshold: 0.2,
    delay: 0,
  });

  const { getItemProps } = useStaggeredScrollAnimation('fade-in-up', {
    threshold: 0.2,
    delay: 100,
  });

  return (
    <div
      ref={titleRef.ref as React.RefObject<HTMLDivElement>}
      style={titleRef.style}
      className="text-center mb-12"
    >
      <h2 className="text-3xl md:text-4xl font-bold">Featured Properties</h2>
      <p className="text-lg text-muted-foreground mt-2">Handpicked properties by our team</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        {properties.map((property, idx) => {
          const itemProps = getItemProps(idx);
          return (
            <div key={property.id} {...itemProps}>
              {idx === 0 ? (
                <PropertyCardPremium property={property} />
              ) : (
                <PropertyCard property={property} />
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-12">
        <Button asChild size="lg">
          <Link href="/properties">View All Properties</Link>
        </Button>
      </div>
    </div>
  );
}
